import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuid } from 'uuid';
import { useSearchParams } from 'react-router-dom';

import { every, filter, maxBy } from 'lodash';
import { IChartData, IChartDetail, IChartDetails, IChartInfo, IChartSettingsType } from '../chartImages/interface/chartImages.interface';
import HttpService from '../../services/http.service';
import { alertType, API_CONFIG, colorPallet, customNumberFormatter } from '../../shared/constants/api';
import StockChart, { ChartAxisPosition } from '../chartImages/components/stockChart';
import Spinner from '../../shared/components/spinner/spinner';
import { useQuery } from '../../shared/components/hooks/useQuery';

import '../../../src/styles/materialChart.scss';
import { CourseExceededIcon, CourseUndershotIcon, NegativeDeviationIcon, PositiveDeviationIcon } from '../../shared/components/icons/icon';


const MaterialChart = () => {
	const params = useQuery();
	const [searchParams] = useSearchParams();
	const language = searchParams.get('language');
	const [chartSettings, setChartSettings] = useState<IChartSettingsType[]>([]);
	const [chartData, setChartData] = useState([] as IChartData[]);
	const [loading, setLoading] = useState(false);
	const [isIntraday, setIsIntraday] = useState(false);
	const [updatedAllChartDetails, setUpdatedAllChartDetails] = useState({} as { [key: string]: IChartDetails });
	const [rearrangedData, setRearrangedData] = useState<any>([]);
	const [updatedChartSettings, setUpdatedChartSettings] = useState<IChartSettingsType[]>([]);

	const { boerse, symbol, course, value, userId, waehrung, authId } = useMemo(() => {
		return {
			boerse: `${params.get('boerse')}`,
			symbol: `${params.get('symbol')}`,
			course: `${params.get('course')}`,
			value: `${params.get('value')}`,
			userId: parseInt(`${params.get('userId')}`),
			authId: `${params.get('uuid')}`,
			waehrung: `${params.get('currency')}`,
		};
	}, [params]);

	const chartOrder = useCallback(
		(data: any) => {
			const convertedArray = chartSettings.map((item, settingIndex) => {
				const maxPrice = maxBy(data, (d: any) => d[`cv${item.symbol}-${item.boerse}-${settingIndex}`]?.l);
				return {
					oldKey: `cv${item.symbol}-${item.boerse}-${settingIndex}`,
					value: maxPrice,
					index: settingIndex,
					symbol: item.symbol,
					boerse: item.boerse
				};
			});
			return convertedArray;
		},
		[chartSettings]
	);

	const fetchChartData = useCallback(
		async (payload: any) => {
			setChartData([]);
			setLoading(true);
			const response = await HttpService.post(`${API_CONFIG.path.basicChartData}/${userId}/chart`, { data: payload, span: '1y' },{ uuid: authId }, '');
			const chartKey = `cv${symbol}-${boerse}-0`;
			const updatedChartData = response.data.filter((data: IChartDetail) => (data[chartKey] as IChartInfo).type === 'actualPrice');
			const chartHighValues = await chartOrder(updatedChartData);
			const sortedData = await [...chartHighValues].sort((a, b) => a.value - b.value);
			const newData = await sortedData.map((entry, index) => ({
				...entry,
				newIndex: index,
				updatedKey: `cv${entry.symbol}-${entry.boerse}-${index}`
			}));
			updatedChartData.forEach((objToUpdate: any) => {
				newData.forEach((update) => {
					const { oldKey, updatedKey } = update;
					if (Object.prototype.hasOwnProperty.call(objToUpdate, oldKey)) {
						objToUpdate[updatedKey] = objToUpdate[oldKey];
						if (oldKey !== updatedKey) {
							delete objToUpdate[oldKey];
						}
					}
				});
			});
			
			setRearrangedData(newData);
			setChartData(updatedChartData);
			setUpdatedAllChartDetails(response.headers);
			setIsIntraday(response.isIntraday);
			setLoading(false);
		},
		[boerse, chartOrder, symbol, userId]
	);

	useEffect(() => {
		const tempArray = [];
		if (!isEmpty(rearrangedData)) {
			for (let i = 0; i < rearrangedData.length; i++) {
				const { index } = rearrangedData[i];
				if (chartSettings[index]) {
					tempArray.push(chartSettings[index]);
				}
			}
		}
		setUpdatedChartSettings(!location.pathname.includes('chartScreen') ? chartSettings : tempArray);
	}, [chartSettings, rearrangedData]);

	useEffect(() => {
		if (!location.pathname.includes('chartScreen')) {
			const payload = [{
						boerse: boerse,
						symbol: symbol,
						waehrung: waehrung
			}];
			
			fetchChartData(payload);
		}
	}, [symbol, boerse, fetchChartData, userId, waehrung]);

	useEffect(() => {
		setChartSettings([
			{
				symbol: symbol,
				boerse: boerse,
				basis_symbol: 'FABT',
				currency: {
					label: '',
					value: ''
				},
				axis: {
					label: 'Left',
					value: 'left'
				},
				color: colorPallet[0],
				disallowed: 0,
				history: {
					label: 'none',
					value: ''
				},
				id: uuid(),
				indicators: [],
				name: '',
				unit: {
					label: '',
					value: ''
				}
			}
		]);
	}, [boerse, symbol]);

	const newChartSettings = chartSettings.map((setting) => ({
		...setting,
		axis: { label: 'Right', value: 'right' } // Updating only the axis
	}));

	const { isLeftAxis, isRightAxis } = useMemo(
		() => ({
			isLeftAxis: filter(newChartSettings, { axis: { value: ChartAxisPosition.LEFT_AXIS } }).length > 0,
			isRightAxis: filter(newChartSettings, { axis: { value: ChartAxisPosition.RIGHT_AXIS } }).length > 0
		}),
		[newChartSettings]
	);

	const onMouseEnter = () => {
		const pageWrapper = document.getElementById('scrollable-wrapper');
		if (pageWrapper) {
			pageWrapper.style.overflow = 'hidden';
		}
	};
	const onMouseLeave = () => {
		const pageWrapper = document.getElementById('scrollable-wrapper');
		if (pageWrapper) {
			pageWrapper.style.overflow = 'auto';
		}
	};

	const ALERT_WHEN_MAPPER: {
		[key: string]: {
			name: string;
			icon: ReactElement;
		};
	} = {
		[alertType.MIN]: {
			name: (language === 'en' && `Course undershot`) || `Kurs unterschritten`,
			icon: <CourseUndershotIcon className='course-icon' />
		},
		[alertType.MAX]: {
			icon: <CourseExceededIcon className='course-icon' />,
			name: (language === 'en' && `Course exceeded`) || `Kurs überschritten`
		},
		[alertType.positive]: {
			icon: <PositiveDeviationIcon className='course-icon' />,
			name: (language === 'en' && `% Course exceeded`) || `% Kurs überschritten`
		},
		[alertType.negative]: {
			icon: <NegativeDeviationIcon className='course-icon' />,
			name: (language === 'en' && `% Course undershot`) || `% Kurs unterschritten`
		}
	};

	return (
		<div className={'chart-screen'}>
			<div className='chart mt-40 bg-color--white'>
				<div className='chart-header'>
					<div className='title'>
						{course && (
							<div className='icons'>
								<div className='chart-icon'>{ALERT_WHEN_MAPPER[course].icon}</div>
							</div>
						)}
						{!isEmpty(updatedAllChartDetails) && !isEmpty(updatedChartSettings) && (
							<div>
								{
									updatedAllChartDetails[`${updatedChartSettings[0].symbol}-${updatedChartSettings[0].boerse}-0`][
										language === 'en' ? 'name_eng' : 'name'
									]
								}
							</div>
						)}
					</div>
					{course && (
						<div className='item-title'>
							<span className={`${course === 'min' || course === 'max' ? 'item-value' : ''}`}>{value}</span>
							<span>{ALERT_WHEN_MAPPER[course].name || '-'}</span>
						</div>
					)}
					{!isEmpty(updatedAllChartDetails) &&
						updatedAllChartDetails[`${updatedChartSettings[0].symbol}-${updatedChartSettings[0].boerse}-0`].disallowed === 0 && (
							<h2 className='chart-price'>
								{customNumberFormatter(
									updatedAllChartDetails[`${updatedChartSettings[0].symbol}-${updatedChartSettings[0].boerse}-0`].price,
									updatedAllChartDetails[`${updatedChartSettings[0].symbol}-${updatedChartSettings[0].boerse}-0`].nkstellen
								) || 0}

								{!['null', null].includes(
									updatedAllChartDetails[`${updatedChartSettings[0].symbol}-${updatedChartSettings[0].boerse}-0`].waehrung
								) && (
									<>
										<span className='font--16px font-weight--500 line-height--22px'>
											{`${
												updatedAllChartDetails[`${updatedChartSettings[0].symbol}-${updatedChartSettings[0].boerse}-0`]
													.waehrung
											} /` || ''}
										</span>
										<span className='font--16px font-weight--500 line-height--22px '>
											{updatedAllChartDetails[`${updatedChartSettings[0].symbol}-${updatedChartSettings[0].boerse}-0`].einheit}
										</span>
									</>
								)}
							</h2>
						)}
				</div>
				<div
					className={`chart-wrapper ${isLeftAxis && isRightAxis ? 'left-right-axis' : isRightAxis ? 'right-axis' : ''}`}
					id={`chart-container-main-${updatedChartSettings[0]?.symbol}-0`}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
				>
					{loading && <Spinner />}
					{isEmpty(chartData) &&
						!loading &&
						!isEmpty(updatedAllChartDetails) &&
						every(Object.values(updatedAllChartDetails[`${updatedChartSettings[0].symbol}-${updatedChartSettings[0].boerse}-0`]), [
							'disallowed',
							0
						]) && <p>No Data found</p>}
					{!isEmpty(chartData) &&
						!isEmpty(updatedChartSettings) &&
						updatedAllChartDetails[`${updatedChartSettings[0].symbol}-${updatedChartSettings[0].boerse}-0`] &&
						!loading && (
							<StockChart
								chartData={chartData}
								chartType={'area'}
								showZoomButton={true}
								loading={loading}
								chartDetails={updatedAllChartDetails[`${updatedChartSettings[0].symbol}-${updatedChartSettings[0].boerse}-0`]}
								cardIndex={0}
								chartName='main'
								isIntraday={isIntraday}
								advanceChartConfig={{
									chartSettings: newChartSettings,
									scaleType: 'price'
								}}
								chartSettings={newChartSettings}
							/>
						)}
				</div>
			</div>
		</div>
	);
};

export default MaterialChart;
